/**
 * Bounded sample reservoir for approximate quantiles (p50/p95).
 *
 * We keep the most recent `cap` samples in a ring and sort on demand. This is
 * an approximation — fine for a live dashboard and parameter sweeps where we
 * care about trends, not exact tail statistics. For exact streaming quantiles a
 * t-digest would be the upgrade, but it is overkill here.
 */
export class Reservoir {
  private readonly buf: number[] = [];
  private idx = 0;
  private count = 0;

  constructor(private readonly cap = 512) {}

  add(sample: number): void {
    this.buf[this.idx] = sample;
    this.idx = (this.idx + 1) % this.cap;
    this.count++;
  }

  get size(): number {
    return Math.min(this.count, this.cap);
  }

  /** Quantile in [0,1]; NaN if empty. p50 = percentile(0.5). */
  percentile(q: number): number {
    const n = this.size;
    if (n === 0) return NaN;
    const sorted = this.buf.slice(0, n).sort((a, b) => a - b);
    const rank = Math.min(n - 1, Math.max(0, Math.round(q * (n - 1))));
    return sorted[rank]!;
  }

  mean(): number {
    const n = this.size;
    if (n === 0) return NaN;
    let sum = 0;
    for (let i = 0; i < n; i++) sum += this.buf[i]!;
    return sum / n;
  }
}
